import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { getIcon } from "@/lib/games";
import { Plus, Copy, Trash2, Pencil, ListChecks, Loader2, Upload, Download } from "lucide-react";
import { toast } from "sonner";

export default function Zones({ event, slug, refresh }) {
  const [zones, setZones] = useState(null);
  const [games, setGames] = useState([]);
  const [edit, setEdit] = useState(null);
  const [qZone, setQZone] = useState(null);

  const load = () => api.get(`/events/${slug}/zones`).then((r) => setZones(r.data));
  useEffect(() => { load(); api.get("/meta/games").then((r) => setGames(r.data)); /* eslint-disable-next-line */ }, [slug]);

  const addZone = async () => {
    const g = games[0];
    await api.post(`/events/${slug}/zones`, { name: `New ${g.name}`, game_type: g.type });
    toast.success("Zone added"); load();
  };
  const dup = async (z) => { await api.post(`/events/${slug}/zones/${z.zone_id}/duplicate`); toast.success("Duplicated"); load(); };
  const del = async (z) => { await api.delete(`/events/${slug}/zones/${z.zone_id}`); toast.success("Deleted"); load(); };

  if (!zones) return <Loader2 className="w-6 h-6 animate-spin text-primary" />;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="font-head font-black text-2xl">Challenge Zones</h1><p className="text-muted-foreground text-sm">The building blocks of your event.</p></div>
        <Button className="rounded-full font-bold" onClick={addZone} data-testid="add-zone"><Plus className="w-4 h-4 mr-1" />Add Zone</Button>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {zones.map((z) => { const I = getIcon(z.game_type === z.game_type ? (games.find(g => g.type === z.game_type)?.icon || "Gamepad2") : "Gamepad2"); return (
          <div key={z.zone_id} className="brut p-5" data-testid={`zone-row-${z.zone_id}`}>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-secondary grid place-items-center"><I className="w-5 h-5 text-primary" /></div>
              <div className="flex-1 min-w-0">
                <h3 className="font-head font-bold text-lg">{z.name}</h3>
                <p className="text-xs text-muted-foreground">{z.mode} · {z.max_xp} Cash · {z.attempts} tries · {z.question_count} questions</p>
                <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{z.description}</p>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <Button size="sm" variant="secondary" className="rounded-full" onClick={() => setEdit(z)} data-testid={`edit-zone-${z.zone_id}`}><Pencil className="w-3.5 h-3.5 mr-1" />Edit</Button>
              <Button size="sm" variant="secondary" className="rounded-full" onClick={() => setQZone(z)} data-testid={`questions-${z.zone_id}`}><ListChecks className="w-3.5 h-3.5 mr-1" />Questions</Button>
              <Button size="sm" variant="ghost" className="rounded-full" onClick={() => dup(z)}><Copy className="w-3.5 h-3.5" /></Button>
              <Button size="sm" variant="ghost" className="rounded-full text-primary" onClick={() => del(z)} data-testid={`del-zone-${z.zone_id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
            </div>
          </div>
        ); })}
      </div>

      {edit && <EditZone slug={slug} zone={edit} games={games} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
      {qZone && <Questions slug={slug} zone={qZone} onClose={() => { setQZone(null); load(); }} />}
    </div>
  );
}

function EditZone({ slug, zone, games, onClose, onSaved }) {
  const [f, setF] = useState({ ...zone });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const save = async () => {
    await api.put(`/events/${slug}/zones/${zone.zone_id}`, {
      name: f.name, description: f.description, location: f.location, game_type: f.game_type,
      instructions: f.instructions, max_xp: Number(f.max_xp), attempts: Number(f.attempts),
      time_limit: Number(f.time_limit), status: f.status,
    });
    toast.success("Saved"); onSaved();
  };
  return (
    <Dialog open onOpenChange={onClose}><DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
      <DialogHeader><DialogTitle className="font-head">Edit Zone</DialogTitle></DialogHeader>
      <div className="space-y-3">
        <div><Label className="mb-1 block">Name</Label><Input value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="zone-edit-name" /></div>
        <div><Label className="mb-1 block">Game type</Label>
          <Select value={f.game_type} onValueChange={(v) => set("game_type", v)}><SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{games.map((g) => <SelectItem key={g.type} value={g.type}>{g.name} ({g.mode})</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label className="mb-1 block">Description</Label><Textarea value={f.description} onChange={(e) => set("description", e.target.value)} /></div>
        <div><Label className="mb-1 block">Location</Label><Input value={f.location || ""} onChange={(e) => set("location", e.target.value)} /></div>
        <div><Label className="mb-1 block">Instructions</Label><Textarea value={f.instructions || ""} onChange={(e) => set("instructions", e.target.value)} /></div>
        <div className="grid grid-cols-3 gap-3">
          <div><Label className="mb-1 block">Max Cash</Label><Input type="number" value={f.max_xp} onChange={(e) => set("max_xp", e.target.value)} /></div>
          <div><Label className="mb-1 block">Attempts</Label><Input type="number" value={f.attempts} onChange={(e) => set("attempts", e.target.value)} /></div>
          <div><Label className="mb-1 block">Time (s)</Label><Input type="number" value={f.time_limit} onChange={(e) => set("time_limit", e.target.value)} /></div>
        </div>
        <div><Label className="mb-1 block">Status</Label>
          <Select value={f.status} onValueChange={(v) => set("status", v)}><SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="available">Available</SelectItem><SelectItem value="locked">Locked</SelectItem></SelectContent>
          </Select>
        </div>
        <Button className="w-full rounded-full font-bold" onClick={save} data-testid="zone-save">Save</Button>
      </div>
    </DialogContent></Dialog>
  );
}

function Questions({ slug, zone, onClose }) {
  const [qs, setQs] = useState([]);
  const [nf, setNf] = useState({ text: "", options: ["", "", "", ""], correct: 0, xp: 100, category: "General" });
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const load = () => api.get(`/events/${slug}/questions`, { params: { zone_id: zone.zone_id } }).then((r) => setQs(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const add = async () => {
    if (!nf.text.trim()) { toast.error("Enter a question"); return; }
    await api.post(`/events/${slug}/questions`, { ...nf, zone_id: zone.zone_id, game_type: zone.game_type, options: nf.options.filter((o) => o.trim()) });
    setNf({ text: "", options: ["", "", "", ""], correct: 0, xp: 100, category: "General" }); load();
  };
  const del = async (id) => { await api.delete(`/events/${slug}/questions/${id}`); load(); };

  const downloadTemplate = async (format) => {
    try {
      const r = await api.get(`/events/${slug}/questions/template`, { params: { format }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `question_import_template.${format === "xlsx" ? "xlsx" : "csv"}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Could not download template"); }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true); setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("zone_id", zone.zone_id);
      const r = await api.post(`/events/${slug}/questions/import`, fd);
      setImportResult(r.data);
      if (r.data.imported > 0) toast.success(`Imported ${r.data.imported} question${r.data.imported === 1 ? "" : "s"}`);
      if (r.data.failed > 0) toast.warning(`${r.data.failed} row${r.data.failed === 1 ? "" : "s"} skipped — see details`);
      if (r.data.imported === 0 && r.data.failed === 0) toast.info("No questions found in file");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    } finally { setImporting(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}><DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
      <DialogHeader><DialogTitle className="font-head">Questions · {zone.name}</DialogTitle></DialogHeader>

      <div className="brut p-4 space-y-3 mb-4" data-testid="q-import-panel">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-head font-bold text-sm">Bulk import</p>
            <p className="text-xs text-muted-foreground">Upload a CSV or Excel file of multiple-choice questions.</p>
          </div>
          <Upload className="w-5 h-5 text-primary" />
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="secondary" className="rounded-full" onClick={() => downloadTemplate("csv")} data-testid="q-template-csv"><Download className="w-3.5 h-3.5 mr-1" />CSV template</Button>
          <Button size="sm" variant="secondary" className="rounded-full" onClick={() => downloadTemplate("xlsx")} data-testid="q-template-xlsx"><Download className="w-3.5 h-3.5 mr-1" />Excel template</Button>
          <label className="inline-flex">
            <input type="file" accept=".csv,.xlsx" className="hidden" onChange={onFile} data-testid="q-import-input" disabled={importing} />
            <span className={`inline-flex items-center px-4 h-9 rounded-full font-bold text-sm cursor-pointer bg-primary text-primary-foreground ${importing ? "opacity-60 pointer-events-none" : ""}`} data-testid="q-import-btn">
              {importing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}Upload file
            </span>
          </label>
        </div>
        <p className="text-[11px] text-muted-foreground leading-snug">Columns: text, option_a–option_d, correct (A–D or 1–4), xp, category, difficulty, time_limit.</p>
        {importResult && (
          <div className="text-xs border-t border-border pt-2" data-testid="q-import-result">
            <p className="font-semibold text-neon-green">{importResult.imported} imported · <span className="text-primary">{importResult.failed} skipped</span> · {importResult.total} rows</p>
            {importResult.errors?.length > 0 && (
              <ul className="mt-1 space-y-0.5 max-h-24 overflow-y-auto text-muted-foreground">
                {importResult.errors.map((er, i) => <li key={i}>Row {er.row}: {er.error}</li>)}
              </ul>
            )}
          </div>
        )}
      </div>

      <div className="space-y-2 mb-4">
        {qs.map((q) => (
          <div key={q.question_id} className="brut p-3 flex items-start justify-between gap-2">
            <div className="text-sm"><p className="font-semibold">{q.text}</p><p className="text-xs text-muted-foreground">✓ {q.options[q.correct]} · {q.xp} Cash · {q.category}</p></div>
            <Button size="icon" variant="ghost" className="text-primary shrink-0" onClick={() => del(q.question_id)}><Trash2 className="w-4 h-4" /></Button>
          </div>
        ))}
        {qs.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">No questions yet.</p>}
      </div>
      <div className="brut p-4 space-y-2">
        <Input placeholder="Question text" value={nf.text} onChange={(e) => setNf({ ...nf, text: e.target.value })} data-testid="q-text" />
        {nf.options.map((o, i) => (
          <div key={i} className="flex items-center gap-2">
            <input type="radio" checked={nf.correct === i} onChange={() => setNf({ ...nf, correct: i })} data-testid={`q-correct-${i}`} />
            <Input placeholder={`Option ${String.fromCharCode(65 + i)}`} value={o} onChange={(e) => { const opts = [...nf.options]; opts[i] = e.target.value; setNf({ ...nf, options: opts }); }} data-testid={`q-opt-${i}`} />
          </div>
        ))}
        <div className="flex gap-2">
          <Input type="number" className="w-24" value={nf.xp} onChange={(e) => setNf({ ...nf, xp: Number(e.target.value) })} placeholder="Cash" />
          <Input value={nf.category} onChange={(e) => setNf({ ...nf, category: e.target.value })} placeholder="Category" />
        </div>
        <Button className="w-full rounded-full font-bold" onClick={add} data-testid="q-add"><Plus className="w-4 h-4 mr-1" />Add Question (correct = selected radio)</Button>
      </div>
    </DialogContent></Dialog>
  );
}
